import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { PARAMETRISED_ROUTES } from './routePaths';

/**
 * Front-end error reporting to the self-hosted GlitchTip (errors.no-gravity.net), through the Sentry SDK.
 *
 * - Errors only. No tracing (the backend follows the `sentry-trace` header of the caller, so front tracing would cut its
 *   traces) and no session replay.
 * - Nothing is sent unless SENTRY_DSN is set at build time (local dev, tests and CI have none).
 * - The release is not set here: on Render builds the esbuild Sentry plugin injects it, so it matches the uploaded source maps.
 *   OTA builds have no token, so their events have no release.
 * - Users and patients are personal data (GDPR): events and breadcrumbs are scrubbed before they leave the browser.
 */

const ROUTE_SEGMENTS = PARAMETRISED_ROUTES.map((route) => route.split('/'));
// An ObjectId, a UUID, a JWT (three dotted parts, its payload holds the user's email) or any other long token. Lowercase words joined
// by hyphens (`set-initial-password`) are endpoint names, not tokens.
const ID_LIKE_SEGMENT =
  /^(?:[0-9a-fA-F]{24}|[0-9a-fA-F]{8}-(?:[0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}|[\w-]{8,}\.[\w-]{8,}\.[\w-]{8,}|(?![a-z]+(?:-[a-z]+)*$)[\w:-]{20,})$/;
const EMBEDDED_OBJECT_ID = /[0-9a-fA-F]{24}/g;
const EMAIL_IN_SEGMENT = /@|%40/i;
const EMAIL_IN_TEXT = /[\w.+-]{1,64}(?:@|%40)[\w-]+(?:\.[\w-]+)+/gi;
const ORIGIN_AND_PATH = /^([a-z][a-z\d+.-]*:\/\/[^/?#]*)?([^?#]*)/i;
// These can carry the whole payload (a camera image as base64, a script): only the scheme is kept.
const PAYLOAD_URL = /^(data|blob|javascript):/i;

/** A route with a parameter gets `:param` in place of each value. A path of no known route only loses its id-like segments. */
function redactPath(path: string): string {
  const clean = path.replace(/\/{2,}/g, '/');
  const segments = (clean.length > 1 && clean.endsWith('/') ? clean.slice(0, -1) : clean).split('/');
  const route = ROUTE_SEGMENTS.find(
    (routeSegments) =>
      routeSegments.length === segments.length &&
      routeSegments.every((segment, index) => segment.startsWith(':') || segment.toLowerCase() === segments[index].toLowerCase()),
  );
  return segments
    .map((segment, index) => {
      if (route) return route[index].startsWith(':') ? route[index] : segment;
      if (EMAIL_IN_SEGMENT.test(segment)) return ':email';
      // An id inside a file name (`1700_consent_<ObjectId>.pdf` in a presigned url) goes, the rest of the name stays.
      return ID_LIKE_SEGMENT.test(segment) ? ':id' : segment.replace(EMBEDDED_OBJECT_ID, ':id');
    })
    .join('/');
}

/** Keeps the origin of an absolute url and drops the query and the fragment. Also works on bare paths. */
const redactUrl = (url: string): string => {
  const payloadScheme = url.match(PAYLOAD_URL);
  if (payloadScheme) return `${payloadScheme[1].toLowerCase()}:`;

  const [, origin = '', path = ''] = url.match(ORIGIN_AND_PATH) ?? [];
  return origin + redactPath(path);
};

/** Free text (an error message, a breadcrumb message) can quote an email. */
const redactEmails = (text: string): string => text.replace(EMAIL_IN_TEXT, ':email');

// `button[aria-label="Edit Jane Doe"]` becomes `button[aria-label]`: these attributes of a clicked element hold people's names.
const ATTRIBUTE_VALUE = /\[(aria-label|title|alt|name)=(?:"[^"]*"|[^\]]*)\]/g;

/** Keeps the message and the stack, drops the request body, cookies, headers and extra data, the query string, route parameters, and any user data but an opaque id. */
function scrubEvent<T extends Sentry.ErrorEvent>(event: T): T {
  const { request, user, extra: _extra, ...rest } = event;
  const scrubbed: T = { ...rest } as T;
  if (scrubbed.message) scrubbed.message = redactEmails(scrubbed.message);
  if (scrubbed.exception?.values) {
    scrubbed.exception = {
      ...scrubbed.exception,
      values: scrubbed.exception.values.map((exception) =>
        exception.value ? { ...exception, value: redactEmails(exception.value) } : exception,
      ),
    };
  }
  if (scrubbed.transaction) scrubbed.transaction = redactUrl(scrubbed.transaction);
  if (request?.url) scrubbed.request = { url: redactUrl(request.url) };
  if (user?.id) scrubbed.user = { id: user.id };
  return scrubbed;
}

/** Redacts request urls and navigation paths (see redactUrl). Console output may hold anything, so it is dropped. */
function scrubBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  if (breadcrumb.category === 'console') return null;
  const withoutAttributes = breadcrumb.category?.startsWith('ui.')
    ? breadcrumb.message?.replace(ATTRIBUTE_VALUE, '[$1]').replace(EMBEDDED_OBJECT_ID, ':id')
    : breadcrumb.message;
  const message = withoutAttributes && redactEmails(withoutAttributes);
  if (!breadcrumb.data) return message === breadcrumb.message ? breadcrumb : { ...breadcrumb, message };

  const data = { ...breadcrumb.data };
  (['url', 'from', 'to'] as const).forEach((key) => {
    if (typeof data[key] === 'string') data[key] = redactUrl(data[key]);
  });
  return { ...breadcrumb, ...(message === undefined ? {} : { message }), data };
}

export function initErrorReporting(): void {
  const dsn = process.env.SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: process.env.ENVIRONMENT,
    // 'web', 'ios' or 'android': the web app and the store apps report to the same project.
    initialScope: { tags: { platform: Capacitor.getPlatform() } },
    sendDefaultPii: false,
    // No sentry-trace or baggage header may be added to the API requests.
    tracePropagationTargets: [],
    // Errors only: the browser session integration reports sessions, not errors.
    integrations: (defaults) => defaults.filter((integration) => integration.name !== 'BrowserSession'),
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
}
